<script lang="ts" setup>

import { useAppConfig } from '#imports';

const props = defineProps<{
    loaderSize: number;
    initialNetwork?: User['network'];
}>();

const {
    QueryWTFApiKey,
    collection
} = useWTFApi();

const isOpenFilter = ref(false);
const { $wagmi } = useNuxtApp();

const {
    isLoading,
    isError,
    watchList,
    changeFilterParams,
    handleWatchList,
    filterParams
} = useWatchList();

const {
    data,
    status
} = useAsyncData(QueryWTFApiKey.COLLECTION_PACK, async () => {
    const uuids = watchList.value.map(item => item.uuid);
    const response = await collection.collectionPack(uuids);
    return response.data;
}, {
    watch: [watchList]
});

const filteredData = computed(() => {
    if (!data.value) return [];

    return data.value.filter((item) => {
        return !filterParams.value.network
            || item.network?.toLowerCase() === filterParams.value.network?.toLowerCase();
    });
});

const showWallet = () => {
    setTimeout(
        () => $wagmi?.wallet.setVisibleOpenWallet(true),
        200
    );
};

const {
    filter: {
        networks: filterNetworksList
    }
} = useAppConfig();

onMounted(() => {
    if (!filterParams.value.network && props.initialNetwork) {
        changeFilterParams({ network: props.initialNetwork });
    }
});

</script>

<template>
  <div class="display-flex justify-start width-full">
    <FiltersPanel v-model:model-value="isOpenFilter">
      <div class="display-flex blocks flex-column pad-24 gap-12">
        <p class="compact-500-accent">
          Blockchain
        </p>
        <ToggleIconGroup :items="filterNetworksList"
                         :model-value="filterParams.network"
                         @update:model-value="val => changeFilterParams({ network: val })"
        />
      </div>
    </FiltersPanel>

    <div class="display-flex flex-column gap-16 grow overflow-hidden">
      <SearchBar v-model="isOpenFilter"
                 type-view="collections"
                 :view-change-grid="false"
                 @on-change-search="val => changeFilterParams({ pattern: val })"
      />
      <GridLayout :data="filteredData"
                  :is-loading="isLoading || status === 'pending'"
                  :is-error="!!isError || status === 'error'"
                  :loader-size="loaderSize"
                  class="width-full"
      >
        <template #cart="{ item }">
          <div class="gap-16 display-flex flex-column card cursor-pointer">
            <div class="card__network">
              <IconMain :size="20"
                        :icon="item.network.toUpperCase()"
              />
            </div>
            <NuxtLink :to="{
                        name: 'collections-slug',
                        params: { slug: item.slug },
                      }"
                      class="width-full height-full card__img"
            >
              <ImgMain :src="item.imageUrl"
                       :alt="item.name"
                       sizes="288px xs:543px sm:284px md:348px lg:266px xl:310px xxl:440px"
              />
            </NuxtLink>
            <div class="display-flex flex-row justify-between align-items-center">
              <span>{{ item.name }}</span>
              <FavoriteIconBtn :size="24"
                               aria-label="remove from favorites"
                               :is-active="true"
                               @favorite-change="handleWatchList(item)"
              />
            </div>
          </div>
        </template>
        <template #loader>
          <LoaderSkeleton class="card__loader" />
        </template>
        <template #empty>
          <span class="align-self-center m-auto compact-600-accent">Watchlist is empty</span>
        </template>
        <template #error>
          <div class="display-flex flex-column items-center">
            <p class="mb-8">
              You need to log in to your wallet to use the watchlist
            </p>
            <button class="unlogged-btn"
                    type="button"
                    @click="showWallet()"
            >
              <span class="compact-500-accent">Connect</span>
            </button>
          </div>
        </template>
      </GridLayout>
    </div>
  </div>
</template>

<style lang="scss" scoped>

.card {
  position: relative;
  box-sizing: border-box;
  padding: 8px;
  transition: background 0.3s ease-in-out;
  border-radius: var(--radius-100, 4px);
  background: var(--layer-floor-1);

  &__network {
    display: flex;
    position: absolute;
    top: 4px;
    right: 4px;
    align-items: center;
    justify-content: center;
    padding: 8px;
    transition: background 0.3s ease-in-out;
    border-bottom-left-radius: 50%;
    background-color: var(--layer-floor-1);
  }

  &__img {
    max-height: 266px;
  }

  &:hover {
    background: var(--layer-floor-2);

    .card__network {
      background: var(--layer-floor-2);
    }
  }

  &__loader {
    //width: 100%;
    height: 340px;
  }
}

.unlogged {
  &-btn {
    height: 52px;
    transition: opacity 0.3s ease-in-out;
    border-radius: var(--control-500-radius, 2px);
    opacity: 1;
    background: var(--bg-brand-mojito-primary, #92fe75);
    cursor: pointer;

    &:hover {
      opacity: 0.8;
    }
  }
}

</style>
